import type React from "react";
import type { BlockBackground, ResponsiveValue } from "../../../types/content-section.types";

type BreakpointKey = "base" | "sm" | "md" | "lg" | "xl";

const BREAKPOINTS: Record<Exclude<BreakpointKey, "base">, string> = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
};

export const sanitizeId = (value: string) =>
  value.replace(/[^a-zA-Z0-9_-]/g, "-");

export const toCssVarName = (blockId: string, token: string) =>
  `--csr-${sanitizeId(blockId)}-${token}`;

const isResponsiveObject = <T,>(
  value?: ResponsiveValue<T>
): value is { base: T; sm?: T; md?: T; lg?: T; xl?: T } =>
  typeof value === "object" && value !== null && "base" in value;

const getResponsiveBreakpointValue = <T,>(
  value: ResponsiveValue<T> | undefined,
  breakpoint: BreakpointKey
): T | undefined => {
  if (value == null) return undefined;
  if (!isResponsiveObject(value)) return breakpoint === "base" ? value : undefined;
  if (breakpoint === "base") return value.base;
  return value[breakpoint];
};

export const buildResponsiveCssVarRules = (
  blockId: string,
  config: {
    outerPaddingY?: ResponsiveValue<string>;
    outerPaddingX?: ResponsiveValue<string>;
    containerWidth?: ResponsiveValue<string>;
  }
) => {
  const safeId = sanitizeId(blockId);
  const selector = `[data-content-block-id="${safeId}"]`;
  const outerPaddingYVar = toCssVarName(blockId, "outer-py");
  const outerPaddingXVar = toCssVarName(blockId, "outer-px");
  const containerWidthVar = toCssVarName(blockId, "container-w");

  const rules: string[] = [];
  const breakpoints: BreakpointKey[] = ["base", "sm", "md", "lg", "xl"];

  for (const bp of breakpoints) {
    const declarations: string[] = [];

    const py = getResponsiveBreakpointValue(config.outerPaddingY, bp);
    if (py) declarations.push(`${outerPaddingYVar}:${py};`);

    const px = getResponsiveBreakpointValue(config.outerPaddingX, bp);
    if (px) declarations.push(`${outerPaddingXVar}:${px};`);

    const cw = getResponsiveBreakpointValue(config.containerWidth, bp);
    if (cw) declarations.push(`${containerWidthVar}:${cw};`);

    if (!declarations.length) continue;

    const css = `${selector}{${declarations.join("")}}`;

    if (bp === "base") {
      rules.push(css);
    } else {
      rules.push(`@media (min-width:${BREAKPOINTS[bp]}){${css}}`);
    }
  }

  return rules.join("\n");
};

export const buildOuterStyle = (
  blockId: string,
  background?: BlockBackground,
  extraStyle?: React.CSSProperties
): React.CSSProperties => ({
  paddingTop: `var(${toCssVarName(blockId, "outer-py")}, 0px)`,
  paddingBottom: `var(${toCssVarName(blockId, "outer-py")}, 0px)`,
  paddingLeft: `var(${toCssVarName(blockId, "outer-px")}, 0px)`,
  paddingRight: `var(${toCssVarName(blockId, "outer-px")}, 0px)`,
  backgroundColor: background?.color,
  backgroundImage: background?.image ? `url("${background.image}")` : undefined,
  backgroundSize: background?.size,
  backgroundRepeat: background?.repeat,
  backgroundPosition: background?.position,
  boxSizing: "border-box",
  width: "100%",
  ...extraStyle,
});

export const buildContainerStyle = (
  blockId: string,
  hasContainerWidth: boolean
): React.CSSProperties | undefined => {
  if (!hasContainerWidth) return undefined;

  return {
    width: "100%",
    maxWidth: `var(${toCssVarName(blockId, "container-w")}, 100%)`,
  };
};
